import type { LngLat } from './types'

const EARTH_RADIUS_M = 6_371_008.8

const rad = (d: number) => (d * Math.PI) / 180

/** Great-circle distance in metres. */
export function haversineM(a: LngLat, b: LngLat): number {
  const dLat = rad(b[1] - a[1])
  const dLng = rad(b[0] - a[0])
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)))
}

/** Length of a polyline in metres. */
export function lineLengthM(path: readonly LngLat[]): number {
  let total = 0
  for (let i = 1; i < path.length; i++) total += haversineM(path[i - 1]!, path[i]!)
  return total
}

/**
 * Point at `distanceM` along the polyline from its first vertex, clamped to the ends.
 * Linear interpolation per segment is accurate to centimetres at cable-segment scales.
 */
export function pointAlong(path: readonly LngLat[], distanceM: number): LngLat {
  if (path.length === 0) throw new RangeError('empty path')
  if (distanceM <= 0 || path.length === 1) return path[0]!
  let walked = 0
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!
    const b = path[i]!
    const seg = haversineM(a, b)
    if (walked + seg >= distanceM) {
      const t = seg === 0 ? 0 : (distanceM - walked) / seg
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
    }
    walked += seg
  }
  return path[path.length - 1]!
}
